import React, { memo } from "react";
import LazyImage from "./LazyImage";
import NoImagePlaceholder from "./NoImagePlaceholder";
import { formatFieldValue, revealDelayStyle } from "../utils/productMode";

/**
 * Product mode's grid layout: a wall of image tiles with one-line details
 * under each. Tapping a tile opens the gallery.
 */

const detailClass = (kind, isDarkMode) => {
  if (kind === "price") return `font-semibold tabular-nums ${isDarkMode ? "text-emerald-400" : "text-emerald-600"}`;
  if (kind === "link") return isDarkMode ? "text-blue-400" : "text-blue-600";
  if (kind === "empty") return isDarkMode ? "text-gray-600" : "text-gray-400";
  return isDarkMode ? "text-gray-300" : "text-gray-700";
};

const ProductTile = memo(({ row, image, index, fields, fit, isDarkMode, selected, onOpenGallery }) => {
  const hasImage = Boolean(image.url);

  return (
    <button
      type="button"
      data-row-id={row.id}
      onClick={() => hasImage && onOpenGallery(row)}
      aria-label={`Row ${row.displayIndex ?? row.id}${hasImage ? ": open images" : ": no images"}`}
      className={`group flex flex-col gap-1 min-w-0 text-left rounded-lg select-none transition-transform duration-150 focus:outline-none animate-list-item-in motion-reduce:animate-none ${
        hasImage ? "cursor-pointer active:scale-[0.97]" : "cursor-default"
      }`}
      style={{ ...revealDelayStyle(index), WebkitTouchCallout: "none", WebkitTapHighlightColor: "transparent" }}
    >
      <div
        className={`relative w-full aspect-square overflow-hidden rounded-lg ring-1 transition-shadow duration-150 ${
          selected ? "ring-2 ring-blue-500/80" : isDarkMode ? "ring-white/10" : "ring-gray-200"
        } ${fit === "contain" ? "bg-white" : isDarkMode ? "bg-white/[0.04]" : "bg-gray-100"}`}
      >
        {hasImage ? (
          // Keyed by URL so a changed image source starts from a clean load state
          // instead of inheriting the previous image's loaded/error flags.
          <LazyImage key={image.url} url={image.url} alt="" className="relative w-full h-full" isDarkMode={isDarkMode} fit={fit} />
        ) : (
          <NoImagePlaceholder isDarkMode={isDarkMode} />
        )}
      </div>
      {fields.map((column) => {
        const { text, kind } = formatFieldValue(row.data?.[column.id], column);
        return (
          <div key={column.id} className={`truncate px-0.5 text-[11px] leading-tight ${detailClass(kind, isDarkMode)}`} title={text}>
            {text}
          </div>
        );
      })}
    </button>
  );
});
ProductTile.displayName = "ProductTile";

const ProductTiles = ({ items, fields, settings, isDarkMode, selectedRowId, onOpenGallery }) => (
  <div className="grid gap-2 px-2 pb-6" style={{ gridTemplateColumns: `repeat(${settings.gridColumns}, minmax(0, 1fr))` }}>
    {items.map(({ row, image }, index) => (
      <ProductTile
        key={row.id}
        row={row}
        image={image}
        index={index}
        fields={fields}
        fit={settings.imageFit}
        isDarkMode={isDarkMode}
        selected={selectedRowId === row.id}
        onOpenGallery={onOpenGallery}
      />
    ))}
  </div>
);

export default ProductTiles;
